// Constraint drawing (style A) from the document's entries, in two layers around the grid lines
// (docs/CONSTRAINTS.md is the contract for the entries):
//   under: shaded cells, odd/even shapes, diagonals, lines and bulbs
//   over:  cages, arrows and circles, edge clues, quadruples, outside clues, labels
// Rules without a drawing (anti-knight, anti-king, non-consecutive, disjoint groups, negative
// "all dots given" entries) are told by the rules text only.
import type { ConstraintEntry, PuzzleDoc, Rc } from '@zinc/shared';
import { memo, useMemo } from 'react';
import type { Geometry } from '../../../lib/geometry';
import { Arrows } from './Arrows';
import { Killers } from './Cages';
import { CellMarks, CellShading, OddEven } from './Cells';
import { CornerLabel } from './CornerLabel';
import { Edges } from './Edges';
import { num, ofType, rcList, text, topLeft } from './geom';
import { Diagonals, Lines, PALE_LINE_TYPES } from './Lines';
import { OutsideClues } from './Outside';
import { Quadruples } from './Quadruples';
import { ENDS } from './style';

/**
 * `either` entries (one of several rules holds) draw each of their options, in grey unless the
 * option has its own colour: a grey line that is renban or entropic, a cell that is a minimum
 * or a maximum. Not one the setter's own drawing shows (`drawn: false`: Such Möhrchen's carrots).
 */
function withOptions(doc: PuzzleDoc): PuzzleDoc {
  const options = doc.constraints
    .filter((e) => e.type === 'either' && Array.isArray(e.options) && e.drawn !== false)
    .flatMap((e) => e.options as ConstraintEntry[])
    .map((o) => ({
      ...o,
      style: { color: ENDS.color, ...(typeof o.style === 'object' && o.style ? o.style : {}) },
    }));
  return options.length ? { ...doc, constraints: [...doc.constraints, ...options] } : doc;
}

export { gridFrame, outsideMargins, type Frame, type Margins } from './geom';
export { ShadingCircles } from './Cells';

export const ConstraintsUnder = memo(function ConstraintsUnder({
  doc: source,
  geometry,
}: {
  doc: PuzzleDoc;
  geometry: Geometry;
}) {
  const doc = useMemo(() => withOptions(source), [source]);
  return (
    <g pointerEvents="none" data-layer="constraints-under">
      <CellShading doc={doc} g={geometry} />
      <OddEven doc={doc} />
      <Diagonals doc={doc} />
      <Lines doc={doc} />
      <CellMarks doc={doc} />
    </g>
  );
});

/** Types whose label has no place of its own: it goes in a corner, like a cage sum. */
const LABELLED = [
  ...PALE_LINE_TYPES,
  'double-arrow',
  'arrow',
  'extra-region',
  'odd',
  'even',
  'minimum',
  'maximum',
  'fortress',
  'clone',
  'magic-square',
  'quadruple',
];
const entryLabel = (e: ConstraintEntry) =>
  text(e, 'label') ||
  (e.type === 'product-line' && typeof e.product === 'number' ? `×${e.product}` : '');
const labelCell = (e: ConstraintEntry) =>
  topLeft(e.type === 'arrow' ? rcList(e, 'circle') : rcList(e));

/**
 * Labels of entries without a place of their own (cage sums, dot values and clue numbers show
 * their `label` in place): top-left of the first cell in reading order, like a cage sum.
 */
function Labels({ doc }: { doc: PuzzleDoc }) {
  return (
    <g>
      {ofType(doc, ...LABELLED).map((e, i) => {
        const label = entryLabel(e);
        const at = labelCell(e);
        return label && at ? <CornerLabel key={i} cell={at} text={label} /> : null;
      })}
    </g>
  );
}

/**
 * Every label drawn in a cell's top-left corner, by cell ("r,c"): cage totals, the labels above,
 * labelled cages from imports. Corner marks move out of their way (Marko, 2026-11-03).
 */
export function cornerLabels(source: PuzzleDoc): Map<string, string> {
  const doc = withOptions(source);
  const out = new Map<string, string>();
  const put = (at: Rc | null, label: string | null) => {
    if (!at || !label) return;
    const k = `${at[0]},${at[1]}`;
    if ((out.get(k)?.length ?? 0) < label.length) out.set(k, label);
  };
  for (const e of ofType(doc, 'killer', 'unshaded-sum')) {
    const sum = num(e, 'sum');
    put(topLeft(rcList(e)), text(e, 'label') ?? (sum === null ? null : String(sum)));
  }
  for (const e of ofType(doc, ...LABELLED)) put(labelCell(e), entryLabel(e));
  for (const c of doc.cosmetics) if (c.kind === 'cage' && c.label) put(topLeft(c.cells), c.label);
  return out;
}

export const ConstraintsOver = memo(function ConstraintsOver({ doc: source }: { doc: PuzzleDoc }) {
  const doc = useMemo(() => withOptions(source), [source]);
  return (
    <g pointerEvents="none" data-layer="constraints-over">
      <Killers doc={doc} />
      <Arrows doc={doc} />
      <Edges doc={doc} />
      <Quadruples doc={doc} />
      <OutsideClues doc={doc} />
      <Labels doc={doc} />
    </g>
  );
});
